/**
 * Servicio para manejar operaciones relacionadas con campañas
 */

export const campaignService = {
  /**
   * Crea una nueva campaña
   * @param {Object} campaignData - Datos de la campaña
   * @param {File} excelFile - Archivo Excel (opcional)
   * @param {File} multimediaFile - Archivo multimedia (opcional)
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async crearCampaign(campaignData, excelFile = null, multimediaFile = null) {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;

      if (!authToken) throw new Error('No hay token de autenticación');

      const formData = new FormData();

      // Mapeo de campos para el nuevo backend NestJS
      formData.append('nombre', campaignData.nombre_campania || campaignData.nombre_campaign);
      formData.append('codigoEmpresa', codigoEmpresa);

      // Manejo de plantilla
      if (campaignData.plantilla) {
        const p = typeof campaignData.plantilla === 'string'
          ? JSON.parse(campaignData.plantilla)
          : campaignData.plantilla;

        // Backend espera plantillaId si existe
        if (p.id) {
          formData.append('plantillaId', p.id);
        } else {
          // Si no hay ID, enviar contenido para crear plantilla automáticamente
          if (p.nombre) {
            formData.append('plantillaNombre', p.nombre);
          }
          if (p.cuerpo) {
            formData.append('plantillaContenido', p.cuerpo);
          }
          if (p.parametros_ejemplo && p.parametros_ejemplo.length > 0) {
            // Convertir parametros_ejemplo a array de nombres
            const parametros = p.parametros_ejemplo.map(param => Object.keys(param)[0]);
            formData.append('plantillaParametros', JSON.stringify(parametros));
          }
        }
      }

      formData.append('upload_audiencia', campaignData.upload_audiencia ? 'true' : 'false');

      if (campaignData.filtros) {
        formData.append('filtrosAudiencia', JSON.stringify(campaignData.filtros));
      }

      // Fecha
      if (campaignData.fecha_hora_envio) {
        formData.append('fechaProgramada', campaignData.fecha_hora_envio);
      }

      if (campaignData.proyectoId) {
        formData.append('proyectoId', campaignData.proyectoId);
      }

      if (campaignData.asesorId) {
        formData.append('asesorId', campaignData.asesorId);
      }

      if (excelFile) formData.append('excel', excelFile);
      if (multimediaFile) formData.append('imagen', multimediaFile);

      // Timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      let response;
      try {
        response = await fetch('/api/campanias/crear', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` },
          body: formData,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') throw new Error('Tiempo de espera agotado.');
        throw fetchError;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error del servidor: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Error creando campaña:', error);
      throw error;
    }
  },

  /**
   * Lista todas las campañas
   * @returns {Promise<Object>} - Respuesta del servidor con las campañas
   */
  async listarCampaigns() {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;

      if (!authToken) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`/api/campanias/lista?codigoEmpresa=${codigoEmpresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Token expirado o inválido');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      // Backend NestJS devuelve array directo, pero frontend espera { success: true, data: [...] } o similar
      // Adaptamos la respuesta
      const data = await response.json();
      console.log('✅ [Campaign] Campañas obtenidas exitosamente:', data);
      return { success: true, data: data };
    } catch (error) {
      console.error('❌ [Campaign] Error al listar campañas:', error);
      throw error;
    }
  },

  /**
   * Obtiene una campaña por ID
   * @param {number} campaignId - ID de la campaña
   * @returns {Promise<Object>} - Respuesta del servidor con la campaña
   */
  async obtenerCampaign(campaignId) {
    try {
      const authToken = localStorage.getItem('token');

      if (!authToken) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`/api/campanias/${campaignId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Token expirado o inválido');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Campaign] Campaña obtenida exitosamente:', data);
      return { success: true, data: data };
    } catch (error) {
      console.error('❌ [Campaign] Error al obtener campaña:', error);
      throw error;
    }
  },

  /**
   * Ejecuta una campaña inmediatamente
   * @param {number} campaignId - ID de la campaña
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async ejecutarCampaign(campaignId) {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;

      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch(`/api/campanias/${campaignId}/lanzar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codigoEmpresa })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error al ejecutar campaña: ${response.status}`);
      }

      const data = await response.json();
      console.log('🚀 [Campaign] Campaña iniciada:', data);
      return data;
    } catch (error) {
      console.error('❌ [Campaign] Error al ejecutar:', error);
      throw error;
    }
  },

  async eliminarCampaign(campaignId) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch(`/api/campanias/eliminar/${campaignId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Campaign] Error al eliminar:', error);
      throw error;
    }
  },

  async cambiarEstadoCampaign(campaignId, estado) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch('/api/campanias/cambiar-estado', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id_campania: campaignId, nuevo_estado: estado })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Campaign] Error al cambiar estado:', error);
      throw error;
    }
  },

  async actualizarCampaign(campaignId, data) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch(`/api/campanias/actualizar/${campaignId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('[Campaign] Error al actualizar:', error);
      throw error;
    }
  },

  async crearPlantillaEnMeta(plantillaId) {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;
      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch('/api/campanias/plantilla/crear-en-meta', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plantillaId, codigoEmpresa: parseInt(codigoEmpresa) })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Campaign] Error creando plantilla en Meta:', error);
      throw error;
    }
  },

  async sincronizarPlantillasMeta() {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;
      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch('/api/campanias/plantillas/sincronizar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codigoEmpresa: parseInt(codigoEmpresa) })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[Campaign] Error sincronizando plantillas:', error);
      throw error;
    }
  },

  async listarPlantillas() {
    try {
      const authToken = localStorage.getItem('token');
      const codigoEmpresa = localStorage.getItem('codigoEmpresa') || 1;

      if (!authToken) throw new Error('No hay token de autenticación');

      const response = await fetch(`/api/campanias/plantillas/lista?codigoEmpresa=${codigoEmpresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data: data };
    } catch (error) {
      console.error('[Campaign] Error al listar plantillas:', error);
      throw error;
    }
  }
};
